import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Crown, Check, CreditCard } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const PERKS = [
  "Carer / family portal — unlimited share links + email invites",
  "Clinician portal with read-only plan + intervention notes",
  "Deterioration trend alerts (7-day mood + urge + distress)",
  "Wellness Recovery Action Plan (WRAP)",
  "Unlimited mood diary history & analytics",
  "Web Push background reminders (works while app is closed)",
];

export default function Billing() {
  const { user, refreshMe } = useAuth();
  const [plan, setPlan] = useState(null);
  const [paypal, setPaypal] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/billing/plan").then((r) => setPlan(r.data));

  useEffect(() => {
    load();
    api.get("/billing/paypal/client-id").then((r) => setPaypal(r.data)).catch(() => {});

    // Handle Stripe return
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/billing/stripe/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Premium activated. Thank you!");
            await refreshMe();
            await load();
            window.history.replaceState({}, "", "/billing");
            return;
          }
          if (data.status === "expired") {
            toast.error("Checkout expired");
            return;
          }
        } catch (err) {
          // Transient — re-poll up to N attempts below. Surface in console for diagnostics.
          console.debug("Stripe status poll attempt failed", err);
        }
        if (attempts++ < 8) setTimeout(poll, 2000);
      };
      poll();
    } else if (params.get("cancelled")) {
      toast("Checkout cancelled");
      window.history.replaceState({}, "", "/billing");
    }
  }, [refreshMe]);

  const buyStripe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/stripe/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start Stripe checkout");
    } finally {
      setLoading(false);
    }
  };

  if (!plan) return <div className="text-slate-500" data-testid="billing-loading">Loading…</div>;

  return (
    <div className="space-y-6 fade-up" data-testid="billing-page">
      <header>
        <div className="flex items-center gap-2"><Crown className="w-6 h-6 text-amber-600" /><h1 className="font-display text-3xl font-bold text-slate-900">SafeHaven Premium</h1></div>
        <p className="text-slate-600 mt-2 max-w-xl">Unlocks the carer + clinician portals, deterioration alerts, WRAP, and background push reminders.</p>
      </header>

      {plan.premium && (
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 text-teal-900" data-testid="billing-active">
          <strong>Premium is active.</strong> Renews on {plan.premium_until ? new Date(plan.premium_until).toLocaleDateString("en-GB") : "—"}.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="text-sm text-slate-500">Monthly</div>
          <div className="font-display text-4xl font-bold text-slate-900 mt-1">£{plan.plan.price_gbp}<span className="text-base text-slate-500 font-medium"> / month</span></div>
          <ul className="mt-4 space-y-2">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <h2 className="font-display text-lg font-bold text-slate-900">Pay with…</h2>

          <button
            data-testid="billing-stripe"
            onClick={buyStripe}
            disabled={loading || plan.premium}
            className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2"
          >
            <CreditCard className="w-4 h-4" /> {loading ? "Redirecting…" : "Card (Stripe)"}
          </button>

          {paypal?.configured ? (
            <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: "GBP", intent: "capture", components: "buttons" }}>
              <div data-testid="billing-paypal-wrap" key={paypal.client_id}>
                <PayPalButtons
                  disabled={plan.premium}
                  style={{ layout: "horizontal", shape: "pill", color: "blue", label: "paypal" }}
                  forceReRender={[plan.premium]}
                  createOrder={async () => {
                    const { data } = await api.post("/billing/paypal/order");
                    return data.order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const r = await api.post(`/billing/paypal/capture/${data.orderID}`);
                      if (r.data.paid) {
                        toast.success("Premium activated via PayPal!");
                        await refreshMe();
                        await load();
                      } else {
                        toast.error("PayPal capture failed");
                      }
                    } catch {
                      toast.error("PayPal capture failed");
                    }
                  }}
                  onError={() => toast.error("PayPal error")}
                />
              </div>
            </PayPalScriptProvider>
          ) : (
            <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">PayPal not configured.</div>
          )}
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
        Premium supports the ongoing development of SafeHaven and the research behind the Stanley &amp; Brown intervention. Cancel any time.
      </div>
    </div>
  );
}
